import { useState, createContext, useContext, useCallback } from "react";

const SampleContext = createContext();

function generateUUID() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant bits
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  // Last resort: Math.random() is not cryptographically safe but keeps the app functional
  // in environments without any crypto support (should not occur in modern browsers).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function SampleProvider(props) {
  const [wasatchSample, setWasatchSample] = useState({
    presumed: "",
    presumedSubDescription: "",
    colour: [],
    texture: [],
    bulk: [],
    trace: [],
    geometry: {},
    sampleImages: [],
  });

  const [device, setDevice] = useState();
  const [scanHistory, setScanHistory] = useState({
    bulk: [],
    trace: [],
  });
  const [reachbackReason, setReachbackReason] = useState({});
  const [submissionKey, setSubmissionKey] = useState(() => generateUUID());

  const setCalibrationDevice = useCallback((calibrationDevice) => {
    setDevice(calibrationDevice);
  }, []);

  const setPresumedSubstance = useCallback((substance, description) => {
    setWasatchSample((prev) => ({
      ...prev,
      presumed: substance,
      presumedSubDescription: description,
    }));
  }, []);

  const setColour = useCallback((colour) => {
    setWasatchSample((prev) => ({ ...prev, colour: colour }));
  }, []);

  const setTexture = useCallback((texture) => {
    setWasatchSample((prev) => ({ ...prev, texture: texture }));
  }, []);

  const setGeometry = useCallback((location) => {
    setWasatchSample((prev) => ({ ...prev, geometry: location }));
  }, []);

  const initWasatchBulk = useCallback((sequences) => {
    setWasatchSample((prev) => ({ ...prev, bulk: sequences }));
  }, []);

  const initWasatchTrace = useCallback((sequences) => {
    setWasatchSample((prev) => ({ ...prev, trace: sequences }));
  }, []);

  const initWasatchBackground = useCallback((sequences) => {
    setWasatchSample((prev) => ({ ...prev, background: sequences }));
  }, []);

  const setScanResults = useCallback((scanType, results) => {
    setScanHistory((prev) => ({
      ...prev,
      [scanType]: results,
    }));
  }, []);

  const addReachbackReason = useCallback((reason) => {
    setReachbackReason((prev) => ({ ...prev, ...reason }));
  }, []);

  const removeReachbackReason = useCallback((key) => {
    setReachbackReason((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  }, []);

  const resetSampleData = useCallback(() => {
    setWasatchSample({
      presumed: "",
      presumedSubDescription: "",
      colour: [],
      bulk: [],
      trace: [],
      texture: [],
      geometry: {},
    });
    setScanHistory({
      bulk: [],
      trace: [],
    });
    setReachbackReason({});
    setSubmissionKey(generateUUID());
  }, []);

  return (
    <SampleContext.Provider
      value={{
        wasatchSample,
        device,
        scanHistory,
        reachbackReason,
        submissionKey,
        setCalibrationDevice,
        setPresumedSubstance,
        setColour,
        setTexture,
        setGeometry,
        initWasatchBulk,
        initWasatchTrace,
        initWasatchBackground,
        resetSampleData,
        setScanResults,
        addReachbackReason,
        removeReachbackReason,
      }}
    >
      {props.children}
    </SampleContext.Provider>
  );
}

export const useSampleContext = () => {
  const {
    wasatchSample,
    device,
    scanHistory,
    reachbackReason,
    submissionKey,
    setCalibrationDevice,
    setPresumedSubstance,
    setColour,
    setTexture,
    setGeometry,
    initWasatchBulk,
    initWasatchTrace,
    initWasatchBackground,
    resetSampleData,
    setScanResults,
    addReachbackReason,
    removeReachbackReason,
  } = useContext(SampleContext);
  return {
    wasatchSample,
    device,
    scanHistory,
    reachbackReason,
    submissionKey,
    setCalibrationDevice,
    setPresumedSubstance,
    setColour,
    setTexture,
    setGeometry,
    initWasatchBulk,
    initWasatchTrace,
    initWasatchBackground,
    resetSampleData,
    setScanResults,
    addReachbackReason,
    removeReachbackReason,
  };
};
